import { useState, useRef } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
} from '@tanstack/react-router';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { ProtectedRoute } from '@/components/protected-route';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import DocumentViewerModal, { DocumentViewerModalRef } from '@/components/document-viewer-modal';

import { selectJobOverviewData, selectDocumentsByJobId, selectSmallWorksByJobId } from '@/store/selectors';
import { addJobState, updateJobState } from '@/store/actions';
import { updateHelpModal, updateNotesModal } from '@/store/slices/appStateSlice';
import type { RootState } from '@/store';
import { View } from '@/components/native-wrapper-ui';

export const Route = createFileRoute('/job/$jobId/$date/health-safety')({
  component: () => (
    <ProtectedRoute>
      <JobHealthAndSafetyPage />
    </ProtectedRoute>
  ),
});

interface Document {
  id: string;
  filename: string;
  job_id: string;
  [key: string]: any;
}

function JobHealthAndSafetyPage() {
  const { jobId, date } = useParams({ from: '/job/$jobId/$date/health-safety' });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const documentViewerRef = useRef<DocumentViewerModalRef>(null);

  // Get job data from Redux
  const { booking, job, jobState } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  );

  // Get health and safety documents for this job
  const healthDocs = useSelector((state: RootState) =>
    selectDocumentsByJobId(state, jobId)
  );

  // Get small works data for this job
  const smallWorksData = useSelector((state: RootState) =>
    selectSmallWorksByJobId(state, jobId)
  );

  const handleGoBack = () => {
    navigate({ to: '/job/$jobId/$date', params: { jobId, date } });
  };

  const handleUpdateJobState = () => {
    if (typeof jobState !== 'undefined') {
      dispatch(updateJobState({
        ...jobState,
        confirmed_health_and_safety: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    } else {
      dispatch(addJobState({
        job_id: job.id,
        confirmed_health_and_safety: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    }

    // Navigate to next step (photos)
    navigate({ to: '/job/$jobId/$date/photos', params: { jobId, date } });
  };

  const handleOpenDocument = (document: Document) => {
    if (documentViewerRef.current) {
      documentViewerRef.current.openDocument(document);
    }
  };

  const handleSmallWorksForm = () => {
    navigate({ to: '/job/$jobId/$date/small-works', params: { jobId, date } });
  };

  const showNotesModal = () => {
    dispatch(updateNotesModal({
      show: true,
      canAssignTask: true,
    }));
  };

  const showHelpModal = () => {
    dispatch(updateHelpModal(true));
  };

  if (!job || !booking) {
    return (
      <div className="col-span-2 flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Job not found</h2>
          <p className="text-gray-500">Could not load job data</p>
        </div>
      </div>
    );
  }

  const isHealthAndSafetyComplete = jobState?.confirmed_health_and_safety !== null;
  const isSmallWorksComplete = smallWorksData?.completed_small_works;

  return (
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex justify-center">
        <div className="max-w-4xl w-full space-y-8">

          <div className="space-y-4">
            <h1 className="text-3xl font-bold leading-tight">
              Please complete your Small Works form
            </h1>

            <p className="text-base leading-relaxed">
              You must complete this checklist before undertaking any works. If the questions raise doubt in your ability to complete the works safely, please contact your Supervisor / Manager to discuss your concerns.
            </p>
          </div>

          {/* Small Works Form Section */}
          <div className="space-y-4 py-8 border-b border-gray-200">
            <div className="flex gap-4">
              <Button
                onClick={handleSmallWorksForm}
                className="flex-1"
              >
                {isSmallWorksComplete
                  ? 'Edit Small Works Form'
                  : 'Complete the Small Works Form'}
              </Button>

              <Button
                variant="secondary"
                onClick={showHelpModal}
                className="flex-1"
              >
                I Need Assistance
              </Button>
            </div>
          </div>

          {/* Health & Safety Documents Section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label className="text-gray-800 font-semibold text-sm uppercase">
                LINKED H&S DOCS
              </Label>
              <Button
                variant="link"
                onClick={() => navigate({
                  to: '/job/$jobId/$date/health-safety-files',
                  params: { jobId, date },
                  search: { allDocsOnly: true }
                })}
                className="text-xs uppercase p-0 h-auto"
              >
                ACCESS ALL DOCS
              </Button>
            </div>

            <div className="border-t border-gray-200 pt-4">
              {healthDocs && healthDocs.length > 0 ? (
                <div className="py-2">
                  {healthDocs.map((document, index) => (
                    <View
                      key={`doc-${index}`}
                      className="cursor-pointer hover:bg-gray-50 py-2 transition-colors"
                      onClick={() => handleOpenDocument(document)}
                    >
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <svg
                              className="w-5 h-5 text-gray-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <span className="text-black m-0">
                            {document.filename}
                          </span>
                        </div>
                    </View>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-gray-600">
                      There are no other health and safety documents linked to this job.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 py-8 border-t border-gray-200">
            {isHealthAndSafetyComplete ? (
              <Button
                onClick={handleGoBack}
                className="flex-1"
              >
                Back to Overview
              </Button>
            ) : (
              <Button
                onClick={handleUpdateJobState}
                className="flex-1"
                disabled={!isSmallWorksComplete}
              >
                Continue to Photos
              </Button>
            )}

            <Button
              variant="secondary"
              onClick={showHelpModal}
              className="flex-1"
            >
              I Need Assistance
            </Button>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      <DocumentViewerModal ref={documentViewerRef} />
    </ScrollArea>
  );
}
